import { ReactNode, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Ban, AlertTriangle } from "lucide-react";
import { AppRole } from "@/types/supabase";
import { LoadingScreen } from "@/components/ui/loading-screen";

interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  allowedRoles?: AppRole[];
}

export function AuthGuard({
  children,
  requireAuth = true,
  allowedRoles,
}: AuthGuardProps) {
  const { user, userRole, isLoading } = useAuth();
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!user) {
        setCheckingStatus(false);
        return;
      }

      try {
        console.log("Checking suspension status for user:", user.id);

        // Check if user is suspended
        const { data, error } = await supabase.rpc("is_user_suspended", {
          p_user_id: user.id,
        });

        console.log("Suspension check response:", { data, error });

        if (error) {
          console.error("Error checking suspension status:", error);
          setCheckingStatus(false);
          return;
        }

        if (data) {
          console.log("User is suspended, getting suspension details");

          // Get suspension details
          const { data: profileData, error: profileError } = await supabase
            .from("public_profiles")
            .select("suspended_reason")
            .eq("id", user.id)
            .single();

          console.log("Profile data response:", { profileData, profileError });

          setIsSuspended(true);
          setSuspensionReason(profileData?.suspended_reason || null);
        } else {
          console.log("User is not suspended");
          setIsSuspended(false);
          setSuspensionReason(null);
        }
      } catch (error) {
        console.error("Error checking user status:", error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkUserStatus();
  }, [user]);

  if (isLoading || checkingStatus) {
    return <LoadingScreen message="Loading your account…" />;
  }

  if (requireAuth && !user) {
    return <Navigate to="/login" replace />;
  }

  // Check role-based access
  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    return (
      <div className="container mx-auto flex items-center justify-center min-h-screen px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to access this page.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate("/dashboard")} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSuspended) {
    console.log("Rendering suspension screen for user:", user?.id);

    return (
      <div className="container mx-auto flex items-center justify-center min-h-screen px-4">
        <Card className="w-full max-w-md bg-warning/10">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Ban className="h-16 w-16 text-warning" />
            </div>
            <CardTitle className="text-warning">Account Suspended</CardTitle>
            <CardDescription className="text-warning/80">
              Your account has been temporarily suspended and you cannot access
              most features.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {suspensionReason && (
              <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/30 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-warning mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-warning mb-1">Reason:</p>
                  <p className="text-warning/80 text-sm">{suspensionReason}</p>
                </div>
              </div>
            )}
            <div className="text-center text-sm text-warning/80">
              <p>If you believe this is an error, please contact support.</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="flex-1 border-warning/50 text-warning hover:bg-warning/20"
              >
                Go Home
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Sign out suspended user
                  supabase.auth.signOut();
                  navigate("/login");
                }}
                className="flex-1 border-warning/50 text-warning hover:bg-warning/20"
              >
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
